import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { COURT_LINKS, COURTS, BOOKING_URL } from "../../mock/mock";
import { fetchCourtsPublic, resolveImg } from "../adminApi";
import { SplitWords } from "../motionHelpers";

export default function CourtsShowcase() {
  const ref = useRef(null);
  const [courtsMap, setCourtsMap] = useState(null);
  useEffect(() => {
    fetchCourtsPublic()
      .then((list) => setCourtsMap(Object.fromEntries(list.map((c) => [c.slug, c]))))
      .catch(() => setCourtsMap({}));
  }, []);
  const getCourt = (s) => (courtsMap && courtsMap[s]) || COURTS[s] || {};
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-68%"]);

  return (
    <section id="courts" ref={ref} className="relative d2-bg" style={{ height: "320vh" }}>
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-[1400px] mx-auto w-full px-5 lg:px-8 mb-8">
          <span className="font-grotesk uppercase tracking-[0.3em] text-sc-lime text-sm">Step Inside</span>
          <h2 className="font-display uppercase text-white text-5xl md:text-7xl leading-[0.9] mt-3">
            <SplitWords text="The Courts" />
          </h2>
        </div>
        <motion.div style={{ x }} className="flex gap-6 pl-5 lg:pl-8 will-change-transform">
          {COURT_LINKS.map((c) => {
            const data = getCourt(c.slug);
            return (
              <Link key={c.slug} to={`/courts/${c.slug}`} data-hover className="group relative shrink-0 w-[78vw] sm:w-[56vw] lg:w-[40vw] h-[62vh] overflow-hidden rounded-2xl">
                <img src={resolveImg(data.hero)} alt={data.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06170f] via-[#06170f]/20 to-transparent" />
                <div className="absolute top-6 left-6 font-display text-6xl text-sc-lime/90">{c.tag}</div>
                <div className="absolute bottom-0 left-0 p-8 right-0">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="font-display uppercase text-4xl text-white">{data.title}</h3>
                      <p className="text-white/70 mt-2 font-grotesk max-w-sm">{data.tagline}</p>
                    </div>
                    <span className="w-12 h-12 rounded-full bg-sc-lime text-[#06170f] flex items-center justify-center shrink-0 group-hover:rotate-45 transition-transform"><ArrowUpRight className="w-6 h-6" /></span>
                  </div>
                </div>
              </Link>
            );
          })}
          <a href={BOOKING_URL} className="shrink-0 w-[78vw] sm:w-[46vw] lg:w-[30vw] h-[62vh] rounded-2xl bg-sc-lime flex flex-col justify-center items-start p-10">
            <h3 className="font-display uppercase text-5xl text-[#06170f] leading-none">Book a Court</h3>
            <p className="text-[#06170f]/70 mt-3 font-grotesk">Grab your paddle and reserve your slot online.</p>
            <span className="d2-btn-ghost border-[#06170f]/40 text-[#06170f] px-7 py-3 mt-6 text-sm inline-flex items-center gap-2">Book Now! <ArrowUpRight className="w-4 h-4" /></span>
          </a>
        </motion.div>
        <div className="max-w-[1400px] mx-auto w-full px-5 lg:px-8 mt-8">
          <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
            <motion.div className="h-full bg-sc-lime" style={{ scaleX: scrollYProgress, transformOrigin: "0% 50%" }} />
          </div>
        </div>
      </div>
    </section>
  );
}
